/**
 * Invite Service for Hugo_Invites Firestore collection.
 *
 * Document structure:
 * - email: string (invited user's email, lowercased)
 * - organizationId: string
 * - organizationName: string (denormalized for display)
 * - role: string ('org_admin' | 'site_manager' | 'department_lead' | 'team_lead' | 'respondent')
 * - scopeId: string | null
 * - scopeType: string ('organization' | 'site' | 'department' | 'team')
 * - status: string ('pending' | 'accepted' | 'declined')
 * - invitedBy: string (UID)
 * - invitedAt: Timestamp
 * - respondedAt: Timestamp | null
 */
import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { logActivity } from '../utils/firestore';
import { assignRole, setToolAccessEmail } from './roleService';

const COLLECTION_NAME = 'Hugo_Invites';

/**
 * Create a new invite.
 * @param {Object} params
 * @param {string} params.email - Invited user's email
 * @param {string} params.organizationId - Organization ID
 * @param {string} params.organizationName - Organization name (denormalized)
 * @param {string} params.role - Role to assign
 * @param {string|null} params.scopeId - Scope ID (for scoped roles)
 * @param {string} params.scopeType - Scope type
 * @param {string} params.invitedBy - UID of inviter
 * @returns {Object} Created invite with ID
 */
export async function createInvite({
  email,
  organizationId,
  organizationName,
  role,
  scopeId,
  scopeType,
  siteId = null,
  deptId = null,
  nwtId = null,
  invitedBy,
  expiresInDays = 14,
}) {
  const normalizedEmail = email.toLowerCase().trim();

  const now = Timestamp.now();
  const expiresAt = Timestamp.fromMillis(now.toMillis() + expiresInDays * 24 * 60 * 60 * 1000);

  const inviteData = {
    email: normalizedEmail,
    organizationId,
    organizationName: organizationName || '',
    role,
    scopeId: scopeId || null,
    scopeType: scopeType || 'organization',
    siteId: siteId || null,
    deptId: deptId || null,
    nwtId: nwtId || null, // P4-22: invites carry the NWT so an IC lands in their team
    status: 'pending',
    invitedBy,
    invitedAt: now,
    expiresAt,
    respondedAt: null,
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), inviteData);

  logActivity(invitedBy, 'user_invited', 'drift-accumulation', {
    email: normalizedEmail,
    organizationId,
    role,
  });

  return { id: docRef.id, ...inviteData };
}

/**
 * Check if a pending invite already exists for this email + org combination.
 * @param {string} email
 * @param {string} organizationId
 * @returns {Object|null} Existing invite or null
 */
export async function findExistingInvite(email, organizationId) {
  const normalizedEmail = email.toLowerCase().trim();
  const q = query(
    collection(db, COLLECTION_NAME),
    where('email', '==', normalizedEmail),
    where('organizationId', '==', organizationId),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

/**
 * Get all pending invites for an organization.
 * @param {string} organizationId
 * @returns {Array} Array of invite objects
 */
export async function getOrganizationInvites(organizationId) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to pending invites for an organization (real-time).
 * @param {string} organizationId
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function subscribeToOrganizationInvites(organizationId, callback) {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('status', '==', 'pending')
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const invites = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(invites);
    },
    (error) => {
      console.warn('Error subscribing to invites:', error.message);
      callback([]);
    }
  );
}

/**
 * Get all pending invites for a user by email.
 * @param {string} email
 * @returns {Array} Array of pending invite objects
 */
export async function getPendingInvitesForUser(email) {
  const normalizedEmail = email.toLowerCase().trim();
  const q = query(
    collection(db, COLLECTION_NAME),
    where('email', '==', normalizedEmail),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Accept an invite: assign the role and mark invite as accepted.
 * @param {string} inviteId - The invite document ID
 * @param {Object} invite - The invite data
 * @param {string} uid - The accepting user's UID
 * @param {string} email - The accepting user's email
 */
export async function acceptInvite(inviteId, invite, uid, email) {
  // Assign the role to the user
  await assignRole(uid, invite.organizationId, invite.role, invite.scopeId, invite.invitedBy, { siteId: invite.siteId || null, deptId: invite.deptId || null, nwtId: invite.nwtId || null });

  // Store email for display
  await setToolAccessEmail(uid, email);

  // Mark invite as accepted
  await updateDoc(doc(db, COLLECTION_NAME, inviteId), {
    status: 'accepted',
    respondedAt: Timestamp.now(),
  });

  logActivity(uid, 'invite_accepted', 'drift-accumulation', {
    organizationId: invite.organizationId,
    role: invite.role,
  });
}

/**
 * Decline an invite.
 * @param {string} inviteId
 * @param {string} uid - The declining user's UID
 */
export async function declineInvite(inviteId, uid) {
  await updateDoc(doc(db, COLLECTION_NAME, inviteId), {
    status: 'declined',
    respondedAt: Timestamp.now(),
  });

  logActivity(uid, 'invite_declined', 'drift-accumulation', {
    inviteId,
  });
}

/**
 * Revoke/cancel a pending invite.
 * @param {string} inviteId
 */
export async function revokeInvite(inviteId) {
  await deleteDoc(doc(db, COLLECTION_NAME, inviteId));
}

/**
 * Check if a user (by email) is already a member of an organization.
 * Checks Hugo_ToolAccess for any document with matching email and org role.
 * @param {string} email
 * @param {string} organizationId
 * @param {Array} existingMembers - Current members list from subscribeToOrganizationMembers
 * @returns {boolean}
 */
export function isAlreadyMember(email, organizationId, existingMembers) {
  const normalizedEmail = email.toLowerCase().trim();
  return existingMembers.some(
    (m) => m.email && m.email.toLowerCase() === normalizedEmail
  );
}
